import React, {useEffect} from 'react';
import {Box, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Outlet, useNavigate, useParams} from 'react-router-dom';
import {useEffectFn} from '@ngneat/effects-hooks';
import {useObservable} from '@ngneat/react-rxjs';
import {emitOnce} from '@ngneat/elf';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';

import {suppliersServicesQuery, suppliersServicesService} from '@store/suppliers/services';
import {searchSuppliersServicesEffect} from '@store/suppliers/services/suppliers-services.effects';
import {SuppliersService} from '@store/suppliers/services/suppliers-services.model';

import SearchComponent from '@components/inputs/search/Search.component';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import NewTableComponent from '@components/table/NewTable.component';

import COLUMN_TYPES from '@utils/table/columnTypes.table';

const SupplierCatalogTable = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {supplierId} = useParams();

  const searchSuppliersServices = useEffectFn(searchSuppliersServicesEffect);

  const [{suppliersServices, loading}] = useObservable(suppliersServicesQuery.suppliersServices$);
  const [selectedSuppliersServices] = useObservable(suppliersServicesQuery.selectedSuppliersServices$);

  const [filters] = useObservable(suppliersServicesQuery.filters$);
  const [sortModel] = useObservable(suppliersServicesQuery.sort$);
  const [tablePagination] = useObservable(suppliersServicesQuery.pagination$);

  useEffect(() => {
    if (supplierId) suppliersServicesService.resetFilters(supplierId);
  }, [supplierId]);

  useEffect(() => {
    searchSuppliersServices({filters, tablePagination, sort: sortModel});
  }, [filters, searchSuppliersServices, sortModel, tablePagination]);

  return (
    <Stack flex={1} spacing={2} pb="25px">
      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Box width="500px">
          <SearchComponent
            value={filters.search}
            handleChange={(value) => {
              emitOnce(() => {
                suppliersServicesService.setFilters('search')(value);
                suppliersServicesService.setPagination('page')(1);
              });
            }}/>
        </Box>
        <TableFiltersComponent
          filters={filters}
          handleSave={suppliersServicesService.setFilters('tableFilters')}
          handleReset={() => supplierId ? suppliersServicesService.resetFilters(supplierId) : undefined}/>
      </Stack>
      <NewTableComponent
        loading={loading}
        columns={[
          COLUMN_TYPES.TEXT_BOLD({field: 'id', label: t('suppliers.supplierServicesTable.id'), width: 100}),
          COLUMN_TYPES.TEXT_BOLD({
            field: 'supplierOrgUnitService.supplierName',
            label: t('suppliers.supplierServicesTable.name'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, SuppliersService>) => params.row.supplierName,
            fullWidth: true,
            minWidth: 350,
          }),
          COLUMN_TYPES.TEXT_BOLD({
            field: 'organisationUnit.name',
            label: t('suppliers.supplierServicesTable.site'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, SuppliersService>) => params.row.supplierUnitName,
            fullWidth: true,
            minWidth: 350,
          }),
          COLUMN_TYPES.TEXT_BOLD({
            field: 'kind.kind',
            label: t('suppliers.supplierServicesTable.serviceKind'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, SuppliersService>) => params.row.kindName,
            width: 250,
          }),
          COLUMN_TYPES.TEXT({
            color: 'secondary',
            field: 'availabilitiesCount',
            label: t('suppliers.supplierServicesTable.availabilities'),
            width: 130,
          }),
          COLUMN_TYPES.TEXT({
            color: 'secondary',
            field: 'container.name',
            label: t('suppliers.supplierServicesTable.container'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, SuppliersService>) => params.row.containerName,
            width: 200,
          }),
          COLUMN_TYPES.TEXT({
            color: 'secondary',
            field: 'waste.name',
            label: t('suppliers.supplierServicesTable.wasteType'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, SuppliersService>) => params.row.wasteName,
            width: 250,
          }),
          COLUMN_TYPES.TEXT({
            color: 'secondary',
            field: 'buyPricesCount',
            label: t('suppliers.supplierServicesTable.buyPrice'),
            valueGetter: (params: GridValueGetterParams<any, SuppliersService>) => params.row.buyPriceIfOnlyOne !== null
              ? `${params.row.buyPriceIfOnlyOne / 100} ${params.row.buyPriceCurrency || ''}`
              : `${params.row.buyPricesCount || '-'}${params.row.buyPricesCount ? ' ' + t('suppliers.details.contractedServices.table.prices') : ''}`,
            width: 130,
          }),
          COLUMN_TYPES.ACTIONS({handleShowRow: (suppliersService: SuppliersService) => navigate(`${suppliersService.id}`)}),
        ]}
        handlePageSizeChange={suppliersServicesService.setPagination('limit')}
        pageSize={tablePagination.limit}
        handlePageChange={(page) => suppliersServicesService.setPagination('page')(page + 1)}
        page={tablePagination.page - 1}
        handleSortModelChange={suppliersServicesService.setSort}
        sortModel={[sortModel]}
        handleRowSelected={suppliersServicesService.setSelectedSuppliersServices}
        rowsSelected={selectedSuppliersServices}
        handleReload={() => searchSuppliersServices({filters, tablePagination, sort: sortModel})}
        totalRows={suppliersServicesQuery.totalRows}
        data={suppliersServices}/>
      <Outlet/>
    </Stack>
  );
};

export default SupplierCatalogTable;
